import React from 'react';

import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import { PERMISSION } from 'src/features/auth';
import { useGetCameras } from 'src/features/cameras/api/getCameras';
import { DashboardWidgetActions } from 'src/features/dashboard/components/DashboardWidgetActions';
import DashboardWidgetCameras from 'src/features/dashboard/components/DashboardWidgetCameras';
import DashboardWidgetMetrics from 'src/features/dashboard/components/DashboardWidgetMetrics';
import DashboardWidgetZones from 'src/features/dashboard/components/DashboardWidgetZones';
import { dashboardMessages } from 'src/features/dashboard/utils/messages';
import EventTable from 'src/features/events/components/EventTable';
import { barrierActions, emergencyBarrierActions } from 'src/features/parkingActions/routes/AllActions';
import { useListOccupancyStatistics } from 'src/features/statistics/api/useListOccupancyStatistics';
import { useListSpacesStatistics } from 'src/features/statistics/api/useListSpacesStatistics';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { useParking } from 'src/providers/ParkingProvider';
import { SectionBlock, SectionBlockHeader } from 'src/registry/components/blocks/SectionBlock';
import { useSidePanel } from 'src/registry/providers/SidePanelProvider';
import { Button } from 'src/registry/ui/Button';

const actions = [...barrierActions, ...emergencyBarrierActions];

const RegularDashboard = () => {
    const { parkingId } = useParking();
    const { sidePanelState } = useSidePanel();
    const isSidePanelOpen = React.useMemo<boolean>(() => sidePanelState['full-log-side-panel'], [sidePanelState]);

    const permissionStats = useHasPermissions([PERMISSION.HOME_STATS]);
    const permissionLog = useHasPermissions([PERMISSION.HOME_LOG]);
    const permissionActions = useHasPermissions([PERMISSION.PARKING_QUICK_ACTIONS]);
    const permissionFullLog = useHasPermissions([PERMISSION.VEHICLE_FULL_LOG]);

    const { formatMessage } = useIntl();

    const {
        data: spacesStatistics,
        isLoading: isLoadingSpacesStatistics,
        errorUpdateCount: errorUpdateCountSpacesStatistics,
    } = useListSpacesStatistics({
        parkingId,
        config: {
            // Let's refetch every 5 seconds, as to reflect log and every minute if failed initially
            refetchInterval: (query) => {
                if (!query.state.error) return 5 * 1000;
                else return 60 * 1000;
            },
            refetchIntervalInBackground: false,
            refetchOnWindowFocus: true,
            retry: false,
            enabled: !!parkingId,
        },
    });

    const today = new Date();
    const lastWeek = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7);

    const {
        data: occupancyStatistics,
        isLoading: isLoadingOccupancyStatistics,
        errorUpdateCount: errorUpdateCountOccupancyStatistics,
    } = useListOccupancyStatistics({
        parkingId,
        from_date: lastWeek.toISOString(),
        config: {
            // Let's refetch every 5 seconds, as to reflect log and every minute if failed initially
            refetchInterval: (query) => {
                if (!query.state.error) return 5 * 1000;
                else return 60 * 1000;
            },
            refetchIntervalInBackground: false,
            refetchOnWindowFocus: true,
            enabled: !!parkingId,
        },
    });

    const {
        data: cameras,
        isLoading: isLoadingCameras,
        isError: isErrorCameras,
    } = useGetCameras({
        parkingId,
        limit: 4,
        offset: 0,
        config: { enabled: !!parkingId },
    });

    const parkingHasCameras = React.useMemo<boolean>(() => !!(cameras?.results && cameras?.results?.length), [cameras]);
    return (
        <>
            <div className="flex flex-col flex-wrap gap-12 sm:flex-row">
                {permissionStats && (
                    <>
                        <DashboardWidgetZones
                            spacesStatistics={spacesStatistics?.results}
                            isLoadingSpacesStatistics={isLoadingSpacesStatistics}
                            errorUpdateCountSpacesStatistics={errorUpdateCountSpacesStatistics}
                        />
                        <DashboardWidgetMetrics
                            spacesStatistics={spacesStatistics?.results}
                            isLoadingSpacesStatistics={isLoadingSpacesStatistics}
                            errorUpdateCountSpacesStatistics={errorUpdateCountSpacesStatistics}
                            occupancyStatistics={occupancyStatistics?.results}
                            isLoadingOccupancyStatistics={isLoadingOccupancyStatistics}
                            errorUpdateCountOccupancyStatistics={errorUpdateCountOccupancyStatistics}
                        />
                    </>
                )}
                {permissionActions && <DashboardWidgetActions actions={actions} />}
                {parkingHasCameras && !isSidePanelOpen && (
                    <DashboardWidgetCameras
                        cameras={cameras?.results}
                        isLoadingCameras={isLoadingCameras}
                        isErrorCameras={isErrorCameras}
                    />
                )}
            </div>
            {permissionLog && (
                <SectionBlock>
                    <SectionBlockHeader className="flex justify-end">
                        {permissionFullLog && (
                            <Button variant="outline" size="sm" asChild>
                                <Link to="/vehicles/full-log">{formatMessage(dashboardMessages.fullLog)}</Link>
                            </Button>
                        )}
                    </SectionBlockHeader>
                    <EventTable isDashboard />
                </SectionBlock>
            )}
        </>
    );
};

export { RegularDashboard };
